/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getUserSubscriberList from "@rc/App/Handlers/EmailPreferences/getUserSubscriberList";
import setUserSubscriberList from "@rc/App/Handlers/EmailPreferences/setUserSubscriberList";
import { UserSubscriberModel } from "@rc/App/Services/EmailPreferences/preferenceCenterUserSubscriberList";
import { xScreen } from "@rc/XDL/Utilities";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface EmailPreferencesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    emailNotificationsTypography?: TypographyProps;
    listStyledWrapper?: InjectableCss;
    itemCheckbox?: CheckboxPresentationProps;
    buttonsWrapper?: InjectableCss;
    orTypography?: TypographyProps;
    modalButtonsWrapper?: InjectableCss;
    unsubscribeLink?: LinkPresentationProps;
}

export const emailPreferencesStyles: EmailPreferencesStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    emailNotificationsTypography: {
        as: "p",
        css: css`
            margin-bottom: 16px;
        `,
    },
    listStyledWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    itemCheckbox: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin: 16px 0;
        `,
    },
    orTypography: {
        css: css`
            margin: 0 8px;
        `,
    },
    modalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 15px;
        `,
    },
    unsubscribeLink: {
        css: css`
            ${xScreen("lte", "xs")} {
                margin-top: 8px;
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    subscriberKey: getSession(state)?.properties?.subscriberKey,
});

const mapDispatchToProps = {
    getUserSubscriberList,
    setUserSubscriberList,
};
type Props = ResolveThunks<typeof mapDispatchToProps> & WidgetProps & ReturnType<typeof mapStateToProps>;

const EmailPreferences: React.FC<Props> = ({ getUserSubscriberList, setUserSubscriberList, subscriberKey }) => {
    const [publicationList, setPublicationList] = useState<UserSubscriberModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isButtonDisablled, setButtonDisablled] = useState(false);
    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        getUserSubscriberList({
            onError: (error: any) => {
                toasterContext.addToast({
                    body: error,
                    messageType: "danger",
                });
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    setPublicationList(resultProps.apiResult.result.publicationList);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
                setIsLoading(false);
            },
        });
    }, []);

    const handleCheckboxClick = (id: string) => {
        const list = publicationList.map(item => {
            if (item.ListId === id) {
                return {
                    ...item,
                    Active: !item.Active,
                    Changed: true,
                };
            }
            return item;
        });
        setPublicationList(list);
    };
    const handleSaveClick = () => {
        setButtonDisablled(true);
        setUserSubscriberList({
            subscriptions: publicationList.map(item => { return {  ...item, Changed: true }}),
            subscriberKey: subscriberKey ?? "",
            onError: (error: any) => {
                setButtonDisablled(false);
                toasterContext.addToast({
                    body: error || translate("Something went wrong. Please try again later!"),
                    messageType: "danger",
                });
            },
            onComplete(resultProps: any) {
                setButtonDisablled(false);
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    toasterContext.addToast({
                        body: translate("Preferences have been Saved successfully!"),
                        messageType: "success",
                    });
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };
    const handleUnsubscribeClick = () => {
        setButtonDisablled(true);
        const list = publicationList.map(item => {
            return {
                ...item,
                Active: false,
                Changed: true,
            };
        });
        setUserSubscriberList({
            subscriptions: list,
            subscriberKey: subscriberKey ?? "",
            onError: error => {
                toasterContext.addToast({
                    body: error != null ? error : translate("Something went wrong. Please try again later!"),
                    messageType: "danger",
                });
                setButtonDisablled(false);
            },
            onComplete(resultProps: any) {
                setButtonDisablled(false);
                setIsModalOpen(false);
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    toasterContext.addToast({
                        body: translate("User Un-Subscribered successfully!"),
                        messageType: "success",
                    });
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    if (isLoading) {
        return (
            <StyledWrapper {...emailPreferencesStyles.centeringWrapper}>
                <LoadingSpinner {...emailPreferencesStyles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    return (
        <>
            <StyledWrapper>
                <Typography {...emailPreferencesStyles.emailNotificationsTypography}>
                    {translate("Email Notifications")}
                </Typography>
                <StyledWrapper {...emailPreferencesStyles.listStyledWrapper}>
                    {publicationList.length > 0 &&
                        publicationList.map(item => (
                            <Checkbox
                                {...emailPreferencesStyles.itemCheckbox}
                                key={item.ListId}
                                onChange={() => {
                                    handleCheckboxClick(item.ListId!);
                                }}
                                checked={item.Active}
                            >
                                {item.ListName}
                            </Checkbox>
                        ))}
                </StyledWrapper>
                <StyledWrapper {...emailPreferencesStyles.buttonsWrapper}>
                    <Button disabled={isButtonDisablled} onClick={handleSaveClick}>
                        {translate("Save Preferences")}
                    </Button>
                    <Typography {...emailPreferencesStyles.orTypography}>or</Typography>
                    <Link {...emailPreferencesStyles.unsubscribeLink} onClick={() => setIsModalOpen(true)}>
                        {translate("Unsubscribe from all communication")}
                    </Link>
                </StyledWrapper>
                <Typography as="p">{siteMessage("Custom_EmailPreferences_termsAndConditions")}</Typography>
            </StyledWrapper>
            <Modal
                headline={translate("Confirm Unsubscribe")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                sizeVariant="small"
            >
                <StyledWrapper>
                    <Typography as="p">
                        {translate("Are you sure you want to unsubscribe from all communication ?")}
                    </Typography>
                    <StyledWrapper {...emailPreferencesStyles.modalButtonsWrapper}>
                        <Link color="primary" onClick={() => setIsModalOpen(false)}>
                            {translate("Cancel")}
                        </Link>
                        <Button disabled={isButtonDisablled} onClick={handleUnsubscribeClick}>
                            {translate("Unsubscribe")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Email Preferences",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EmailPreferences),
    definition,
};

export default widgetModule;
